import { DependencyList, useCallback, useMemo } from 'react'
import {
  ApiGetWithToken,
	useApiFetchList,
	useApiFetch
} from 'shared/api'
import { ApiAccess } from 'features/session/types'
import {
	articleApi,
	searchApiBy,
	searchsAuthorApiBy,
	searchsSisetuApiBy
} from 'interface/endpoints'
import {
	ArticleResult,
	ArticleResultList
} from './types'
import { CancelToken } from 'axios'
import { safeParseDate } from 'shared/util'
import { useUpdateTime } from 'interface/util'

export * from './types'

export const useListArticleBySearch = (
	searchId: number | undefined | null,
	option: {
		updateTime?: string
		partialLimit?: number
		onPreFetch?: () => void
		onFetched?: (result: ArticleResult[]) => void
	} = {},
	deps?: DependencyList
) => {
	const { updateTime, partialLimit, onPreFetch, onFetched } = option
	const cacheTime = useMemo(() => safeParseDate(updateTime)?.getTime(), [updateTime])
	return useApiFetchList<ArticleResult, ArticleResult[]>(
		useCallback(
			(apiAccess, cancelToken) => {
				if (!searchId) return 0
				if (onPreFetch) onPreFetch()
				return ApiGetWithToken<number>(
					apiAccess,
					searchApiBy(searchId, '/article/count', { t: cacheTime }),
					cancelToken
				)
			},
			[searchId, cacheTime, onPreFetch]
		),
		useCallback(
			(apiAccess, cancelToken, offset, limit) => {
				if (!searchId) return null
				const wrapper = async () => {
					const list = await ApiGetWithToken<ArticleResultList>(
						apiAccess,
						searchApiBy(searchId, '/article/list', { t: cacheTime, offset, limit }),
						cancelToken
					)
					return list?.list
				}
				return wrapper()
			},
			[searchId, cacheTime]
		),
		useCallback(
			(result) => {
				if (onFetched) onFetched(result)
				return result
			},
			[onFetched]
		),
		partialLimit ?? 1000,
		deps
	)
}

export const useListArticleByAuthor = (
	searchId: number | undefined | null,
	authorId: number | undefined | null,
	option: {
		updateTime?: string
		partialLimit?: number
		onPreFetch?: () => void
		onFetched?: (result: ArticleResult[]) => void
	} = {},
	deps?: DependencyList
) => {
	const { updateTime, partialLimit, onPreFetch, onFetched } = option
	const t = useUpdateTime(updateTime)
	return useApiFetchList<ArticleResult, ArticleResult[]>(
		useCallback(
			(apiAccess, cancelToken) => {
				if (!searchId || !authorId) return 0
				if (onPreFetch) onPreFetch()
				return ApiGetWithToken<number>(
					apiAccess,
					searchsAuthorApiBy(searchId, authorId, '/article/count', { t }),
					cancelToken
				)
			},
			[searchId, t, authorId, onPreFetch]
		),
		useCallback(
			(apiAccess, cancelToken, offset, limit) => {
				if (!searchId || !authorId) return null
				const wrapper = async () => {
					const list = await ApiGetWithToken<ArticleResultList>(
						apiAccess,
						searchsAuthorApiBy(searchId, authorId, '/article/list', { t, offset, limit }),
						cancelToken
					)
					return list?.list
				}
				return wrapper()
			},
			[searchId, t, authorId]
		),
		useCallback(
			(result) => {
				if (onFetched) onFetched(result)
				return result
			},
			[onFetched]
		),
		partialLimit ?? 1000,
		deps
	)
}

export const ListArticleByAuthor = (
	apiAccess: ApiAccess,
	searchId: number,
	authorId: number,
	option: {
		isDemo?: boolean
		updateTime?: string
		offset?: number
		limit?: number
	} = {},
	cancelToken?: CancelToken
) => {
	const { updateTime, isDemo, offset, limit } = option
	const t = isDemo ? `demo_${safeParseDate(updateTime)?.getTime()}` : safeParseDate(updateTime)?.getTime()
	return ApiGetWithToken<ArticleResultList>(
		apiAccess,
		searchsAuthorApiBy(searchId, authorId, '/article/list', { t, offset, limit }),
		cancelToken
	)
}

export const useListArticleBySisetu = (
	searchId: number | undefined | null,
	sisetuCode: string | undefined | null,
	option: {
		updateTime?: string,
		partialLimit?: number
		onPreFetch?: () => void
		onFetched?: (result: ArticleResult[]) => void
	} = {},
	deps?: DependencyList
) => {
	const { updateTime, partialLimit, onPreFetch, onFetched } = option
	const t = useUpdateTime(updateTime)
	return useApiFetchList<ArticleResult, ArticleResult[]>(
		useCallback(
			(apiAccess, cancelToken) => {
				if (!searchId || !sisetuCode) return 0
				if (onPreFetch) onPreFetch()
				return ApiGetWithToken<number>(
					apiAccess,
					searchsSisetuApiBy(searchId, sisetuCode, '/article/count', { t }),
					cancelToken
				)
			},
			[searchId, t, sisetuCode, onPreFetch]
		),
		useCallback(
			(apiAccess, cancelToken, offset, limit) => {
				if (!searchId || !sisetuCode) return null
				const wrapper = async () => {
					const list = await ApiGetWithToken<ArticleResultList>(
						apiAccess,
						searchsSisetuApiBy(searchId, sisetuCode, '/article/list', { t, offset, limit }),
						cancelToken
					)
					return list?.list
				}
				return wrapper()
			},
			[searchId, t, sisetuCode]
		),
		useCallback(
			(result) => {
				if (onFetched) onFetched(result)
				return result
			},
			[onFetched]
		),
		partialLimit ?? 1000,
		deps
	)
}

export const useGetArticle = (
	searchId: number | null | undefined,
	articleId: number | null | undefined,
	option: {
		defaultArticle?: ArticleResult
		updateTime?: string
	} = {}
) => {
	const { defaultArticle, updateTime } = option
	const t = useUpdateTime(updateTime)
	return useApiFetch(
		useCallback(
			(apiAccess, cancelToken) => {
				if (!searchId || !articleId) return null
				if (defaultArticle && defaultArticle.articleId === articleId) return defaultArticle
				return ApiGetWithToken<ArticleResult>(
					apiAccess,
					searchApiBy(searchId, `/article/${articleId}`, { t }),
					cancelToken
				)
			},
			[searchId, t, articleId, defaultArticle]
		)
	)
}

export const useGetArticleLinkOut = (
	articleId: number | null | undefined,
	option: {
		updateTime?: string
	} = {}
) => {
	const t = useUpdateTime(option.updateTime)
	return useApiFetch(
		useCallback(
			(apiAccess, cancelToken) => {
				if (!articleId) return null
				return ApiGetWithToken<ArticleResult>(
					apiAccess,
					articleApi(`/${articleId}/linkOut`, { t }),
					cancelToken
				)
			},
			[articleId, t]
		)
	)
}